import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View, TextInput } from 'react-native';
import Svg, { Circle, Polyline, Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { CategoriesTrendRowData } from './types';
import { exportCategoryTrendData } from '../pages/TicketingAnalytics/ticketingAnalyticsKpiApi';
import SkeletonBlock from './SkeletonBlock';

interface CategoryTrendSectionProps {
  rows: CategoriesTrendRowData[];
  selectedCategoryLabel: string | null;
  onCategorySelect: (categoryId: number | null) => void;

  /**
   * When true the table body is replaced by shimmering placeholder rows that
   * mirror the real column layout, so the card keeps its size while loading.
   */
  loading?: boolean;

  /** Number of placeholder rows rendered while loading. */
  skeletonRowCount?: number;
  hasActiveFilters?: boolean;
}

const DEFAULT_WEEK_HEADERS = ['W1', 'W2', 'W3', 'W4'];

/**
 * Placeholder rows shown while the trend table loads.
 *
 * Kept in this file so any consumer gets the skeleton simply by passing
 * `loading`, and so it can reuse the exact row / column-width styles below.
 */
const TrendSkeleton: React.FC<{ rowCount: number; weekCount: number }> = ({ rowCount, weekCount }) => (
  <>
    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <View style={styles.row} key={`trend-skeleton-row-${rowIndex}`}>
        <View style={[styles.skeletonCell, styles.categoryTouchable]}>
          <SkeletonBlock style={styles.skeletonCategory} />
        </View>

        {Array.from({ length: weekCount }).map((__, weekIndex) => (
          <View
            key={`trend-skeleton-week-${rowIndex}-${weekIndex}`}
            style={[styles.skeletonCell, styles.skeletonNumCell]}
          >
            <SkeletonBlock style={styles.skeletonNum} />
          </View>
        ))}

        <View style={[styles.skeletonCell, styles.skeletonTrendCell]}>
          <SkeletonBlock style={styles.skeletonSpark} />
        </View>
      </View>
    ))}
  </>
);


const getSparklineMeta = (values: number[], backendPercentageChange?: number) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values
    .map((value, index) => {
      const x = 6 + index * 24;
      const y = 21 - ((value - min) / range) * 16;
      return `${x},${y}`;
    })
    .join(' ');

  // Use backend's percentageChange if available, otherwise calculate from values
  let slope: number;
  let change: number;
  
  if (backendPercentageChange !== undefined) {
    // Backend provides percentageChange as-is (e.g., 300 for 300%, -80 for -80%)
    slope = Math.round(backendPercentageChange);
    change = backendPercentageChange / 100; // Convert to decimal for color logic
  } else {
    // Fallback to calculation from values
    change = (values[values.length - 1] - values[0]) / (values[0] || 1);
    slope = Math.round(change * 100);
  }
  
  const color = change > 0.08 ? '#C12C01' : change < -0.08 ? '#027C06' : '#C59C1B';
  const direction = slope > 0 ? 'up' : slope < 0 ? 'down' : 'flat';
  const last = values[values.length - 1];
  const lastX = 6 + (values.length - 1) * 24;
  const lastY = 21 - ((last - min) / range) * 16;

  return {
    points,
    color,
    direction,
    slope,
    lastX,
    lastY,
  };
};

const TrendSection: React.FC<CategoryTrendSectionProps> = ({
  rows,
  selectedCategoryLabel: _selectedCategoryLabel,
  onCategorySelect: _onCategorySelect,
  loading = false,
  skeletonRowCount = 6,
  hasActiveFilters = false,
}) => {
  const [trendDrill, setTrendDrill] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [hoveredTrendCell, setHoveredTrendCell] = useState<string | null>(null);
  // Card-level tooltip state — tracks mouse coords and text for a single
  // fixed-position tooltip rendered outside the ScrollView so it is never
  // clipped by overflow or offset by scroll position.
  const [tooltipText, setTooltipText] = useState<string | null>(null);
  const [tooltipClientPos, setTooltipClientPos] = useState<{ x: number; y: number } | null>(null);

  const getClientPos = (e: any): { x: number; y: number } | null => {
    try {
      const ne = e?.nativeEvent ?? e ?? {};
      const cx = e?.clientX ?? ne.clientX;
      const cy = e?.clientY ?? ne.clientY;
      if (typeof cx === 'number' && typeof cy === 'number') return { x: cx, y: cy };
    } catch { /* ignore */ }
    return null;
  };
  const [searchText, setSearchText] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSortCategory = () => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));

  const weekHeaders = rows[0]?.weeks.map((item) => item.label) ?? DEFAULT_WEEK_HEADERS;

  const drillRows = useMemo<CategoriesTrendRowData[]>(() => {
    if (!trendDrill) {
      return rows;
    }

    // Find the parent category and use its subCategories
    const parentRow = rows.find((row) => row.category === trendDrill);
    console.log(`[TrendSection] Drilling into "${trendDrill}":`, {
      found: !!parentRow,
      subCategoriesCount: parentRow?.subCategories?.length ?? 0,
      subCategoryNames: parentRow?.subCategories?.map(s => s.name) ?? []
    });
    
    const subCats = parentRow?.subCategories ?? [];
    
    // If category has no subcategories, return empty to show back button
    if (subCats.length === 0) {
      console.warn(`[TrendSection] No subcategories found for "${trendDrill}"`);
      return [];
    }

    return subCats.map((item) => ({
      category: item.name,
      weeks: item.weeks,
    }));
  }, [rows, trendDrill]);

  const processedRows = useMemo<CategoriesTrendRowData[]>(() => {
    const base = trendDrill ? drillRows : rows;
    let list = base.slice();
    const q = searchText.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => String(r.category ?? '').toLowerCase().includes(q));
    }
    list.sort((a, b) => (sortDirection === 'asc' ? String(a.category).localeCompare(String(b.category)) : String(b.category).localeCompare(String(a.category))));
    return list;
  }, [rows, drillRows, trendDrill, searchText, sortDirection]);

  const title = trendDrill ? `${trendDrill} Trend (Rolling 4 Weeks)` : 'Category Trend (Rolling 4 Weeks)';

  const handleExport = async () => {
    try {
      setIsExporting(true);
      console.log('[TrendSection] Starting export for 52 weeks');
      console.log('[TrendSection] drillRows:', drillRows);
      
      // Build category ID to name mapping from the data
      const categoryMapping: Record<string, string> = {};
      
      // Map all visible rows (including drill-down subcategories)
      drillRows.forEach((row) => {
        console.log('[TrendSection] Processing row:', {
          category: row.category,
          categoryId: row.categoryId,
          type: typeof row.categoryId
        });
        
        // Use categoryId if available, otherwise use category as fallback
        if (row.categoryId !== undefined && row.categoryId !== null) {
          const idKey = String(row.categoryId);
          categoryMapping[idKey] = row.category;
          console.log(`[TrendSection] Mapped ID ${idKey} -> ${row.category}`);
        } else {
          console.warn(`[TrendSection] No categoryId for ${row.category}`);
        }
      });
      
      // If in drill-down mode, also include the parent category
      if (trendDrill && rows.length > 0) {
        const parentCategory = rows.find(r => r.category === trendDrill);
        if (parentCategory && parentCategory.categoryId) {
          const idKey = String(parentCategory.categoryId);
          categoryMapping[idKey] = parentCategory.category;
          console.log(`[TrendSection] Mapped parent ID ${idKey} -> ${parentCategory.category}`);
        }
      }
      
      console.log('[TrendSection] Final category ID to name mapping:', categoryMapping);
      console.log('[TrendSection] Mapping keys:', Object.keys(categoryMapping));
      console.log('[TrendSection] Mapping values:', Object.values(categoryMapping));
      
      // Pass category mapping to export function
      await exportCategoryTrendData(52, categoryMapping);
      console.log('[TrendSection] Export completed successfully');
    } catch (error) {
      console.error('[TrendSection] Export failed:', error);
      // Error handling - in a real app, you might show a toast or alert
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Failed to export: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Determine visible rows (filtered + sorted)
  const visibleRows = processedRows;
  const hasVisibleRows = (visibleRows ?? []).length > 0;
  const isAllZero = hasVisibleRows && visibleRows.every((r) => r.weeks.every((w) => (w.value ?? 0) === 0));
  const rowsToRender = loading ? [] : (isAllZero ? [] : visibleRows);

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <View style={styles.headerLeft}>
          {trendDrill ? (
            <TouchableOpacity style={styles.backArrow} onPress={() => setTrendDrill(null)}>
              <Text style={styles.backArrowText}>{'<'}</Text>
            </TouchableOpacity>
          ) : null}
          <Text style={styles.cardTitle}>{title}</Text>
        </View>

        <View style={styles.headerRight}>
          <View style={styles.searchWrap}>
            <Svg width={15} height={15} viewBox="0 0 24 24" style={styles.searchIcon}>
              <Path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="#818181" />
            </Svg>
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder={trendDrill ? 'Search sub-category' : 'Search category'}
              placeholderTextColor="#8A8E94"
              style={styles.searchInput}
            />
          </View>

          <TouchableOpacity 
            style={styles.exportButton} 
            onPress={handleExport}
            disabled={isExporting || loading}
          >
            <Text style={styles.exportButtonText}>
              {isExporting ? '⏳ Exporting...' : '⬇ Export (52 wk)'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.note}>
        Trailing 4 completed weeks. Independent of the time period selector. Select a category to view its
        sub-categories.
      </Text>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
        <View style={styles.table}>
          <View style={[styles.row, styles.headerRow]}>
            <View style={[styles.cell, styles.categoryCell]}>
              <TouchableOpacity onPress={handleSortCategory} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <Text style={[styles.headerCell]}>{trendDrill ? 'Sub Category' : 'Category'}</Text>
                <Text style={[styles.headerCell]}>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</Text>
              </TouchableOpacity>
            </View>
            {weekHeaders.map((label) => (
              <View key={label} style={[styles.cell, styles.numCell]}>
                <Text style={[styles.headerCell, styles.headerCenterText]}>{label}</Text>
              </View>
            ))}
            <View style={[styles.cell, styles.trendCell]}>
              <Text style={[styles.headerCell, styles.headerCenterText]}>Trend</Text>
            </View>
          </View>

          {loading ? (
            <TrendSkeleton rowCount={skeletonRowCount} weekCount={weekHeaders.length} />
          ) : null}

          {!loading && drillRows.length === 0 && trendDrill ? (
            <View style={styles.row}>
              <Text style={[styles.cell, styles.categoryCell, styles.emptyMessage]}>
                No sub-categories available
              </Text>
            </View>
          ) : null}

          {!loading && !trendDrill && rowsToRender.length === 0 ? (
            <View style={styles.noDataWrap}>
              <Text style={styles.noDataText}>
            {hasActiveFilters
              ? 'Try expanding your date range or removing some filters.'
              : 'Try expanding your date range or removing some filters.'}
              </Text>
            </View>
          ) : null}

          {rowsToRender.map((item) => {
            // Use the backend's percentageChange value directly
            const spark = getSparklineMeta(item.weeks.map((week) => week.value), item.percentageChange);
            const canDrill = !trendDrill; // Allow drill-down when not already drilling

            return (
              <View style={styles.row} key={item.category}>
                <TouchableOpacity
                  style={[styles.cell, styles.categoryTouchable]}
                  disabled={!canDrill}
                  onPress={() => setTrendDrill(item.category)}
                >
                  <Text style={canDrill ? styles.categoryLink : styles.categoryText}>{item.category}</Text>
                </TouchableOpacity>

                {item.weeks.map((week) => (
                  <View key={`${item.category}-${week.label}`} style={[styles.cell, styles.numCell]}>
                    <Text style={styles.dataText}>{week.value}</Text>
                  </View>
                ))}

                <View
                  style={[styles.cell, styles.trendCell, styles.trendCellContainer]}
                  {...({
                    onMouseEnter: (e: any) => {
                      const pos = getClientPos(e);
                      if (pos) setTooltipClientPos(pos);
                      const sign = spark.slope > 0 ? '+' : '';
                      setTooltipText(`${sign}${spark.slope}% over 4 weeks`);
                      setHoveredTrendCell(item.category);
                    },
                    onMouseMove: (e: any) => {
                      const pos = getClientPos(e);
                      if (pos) setTooltipClientPos(pos);
                    },
                    onMouseLeave: () => {
                      setHoveredTrendCell(null);
                      setTooltipText(null);
                      setTooltipClientPos(null);
                    },
                  } as any)}
                >
                  <View style={styles.trendWrap}>
                    <Svg width={84} height={26} viewBox="0 0 84 26" style={styles.spark}>
                      <Polyline
                        points={spark.points}
                        fill="none"
                        stroke={spark.color}
                        strokeWidth={2}
                        strokeLinecap="round"
                      />
                      <Circle cx={spark.lastX} cy={spark.lastY} r={2.6} fill={spark.color} />
                    </Svg>
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      {/* Fixed-position tooltip rendered outside the ScrollView so it is
          never clipped or offset by scroll position. Coordinates come from
          the mouse event (clientX/clientY = viewport-relative) so
          `position: fixed` places it exactly next to the cursor. */}
      {hoveredTrendCell && tooltipText && tooltipClientPos && (() => {
        const padding = 8;
        const offsetX = 12;
        const offsetY = 36;
        const winW = typeof window !== 'undefined' ? window.innerWidth : 0;
        const winH = typeof window !== 'undefined' ? window.innerHeight : 0;
        const tooltipWidthEstimate = 180;
        let left = tooltipClientPos.x + offsetX;
        if (winW) left = Math.min(left, Math.max(padding, winW - tooltipWidthEstimate - padding));
        let top = tooltipClientPos.y - offsetY;
        if (winH) top = Math.min(Math.max(padding, top), winH - padding);
        return (
          <View
            pointerEvents="none"
            style={[styles.tooltipFixed, { left, top } as any]}
          >
            <Text style={styles.tooltipText}>{tooltipText}</Text>
          </View>
        );
      })()}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 6,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  backArrow: {
    width: 26,
    height: 26,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backArrowText: {
    fontSize: 14,
    color: '#5D636B',
    fontFamily: FontFamily.bold,
    marginTop: -1,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  exportButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#005CBC',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  exportButtonText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#005CBC',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchWrap: {
    width: 260,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 34,
    backgroundColor: '#FFFFFF',
  },
  searchIcon: {
    flexShrink: 0,
  },
  searchInput: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 0,
    flex: 1,
  },
  note: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#8B92A1',
    marginBottom: 16,
    lineHeight: 18,
  },
  table: {
    minWidth: 900,
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2F6',
    minHeight: 52,
    paddingHorizontal: 0,
  },
  headerRow: {
    borderBottomWidth: 1,
    borderBottomColor: '#D9DFE8',
  },
  cell: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#2D2F33',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  headerCell: {
    fontSize: 11,
    color: '#6A6D71',
    fontFamily: FontFamily.medium,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  headerCenterText: {
    textAlign: 'center',
  },
  dataText: {
    textAlign: 'center',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
  },
  categoryCell: {
    flex: 2,
    minWidth: 280,
    paddingLeft: 0,
    paddingRight: 16,
  },
  categoryTouchable: {
    flex: 2,
    minWidth: 280,
  },
  categoryLink: {
    color: '#005CBC',
    fontFamily: FontFamily.medium,
    textDecorationLine: 'underline',
  },
  categoryText: {
    color: '#2D2F33',
    fontFamily: FontFamily.regular,
  },
  numCell: {
    flex: 1,
    minWidth: 80,
    textAlign: 'center',
    paddingHorizontal: 12,
  },
  trendCell: {
    flex: 1,
    minWidth: 130,
    textAlign: 'center',
    paddingRight: 16,
  },
  trendWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    justifyContent: 'center',
  },
  trendIcon: {
    fontSize: 9,
    width: 10,
    textAlign: 'center',
    fontFamily: FontFamily.bold,
  },
  spark: {
    marginTop: 1,
  },
  up: {
    color: '#C12C01',
  },
  down: {
    color: '#027C06',
  },
  flat: {
    color: '#C59C1B',
  },
  emptyMessage: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  noDataWrap: {
    width: '100%',
    paddingVertical: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#7A8088',
    textAlign: 'center',
  },
  trendCellContainer: {
    position: 'relative' as const,
  },
  // Fixed-position tooltip — rendered at the root card level so it escapes
  // any overflow:hidden or scroll container. Uses position:'fixed' so
  // left/top are set to viewport (clientX/clientY) coordinates.
  tooltipFixed: {
    position: 'fixed' as any,
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  tooltipText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#FFFFFF',
    lineHeight: 16,
  },

  // ─── Loading skeleton ────────────────────────────────────────────────────
  // `numCell` / `trendCell` carry `textAlign`, which is invalid on a View, so
  // the skeleton uses width-only wrappers that centre their placeholder bar.
  skeletonCell: {
    paddingVertical: 12,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  skeletonCategory: {
    width: 180,
    height: 12,
  },
  skeletonNumCell: {
    minWidth: 80,
    alignItems: 'center',
  },
  skeletonNum: {
    width: 26,
    height: 12,
  },
  skeletonTrendCell: {
    minWidth: 130,
  },
  skeletonSpark: {
    width: 84,
    height: 20,
  },
});

export default TrendSection;






